/**
 * Help Page Skeleton
 * Matches the tabbed layout of the redesigned help center page
 *
 * Layout:
 * - Container: h-full overflow-y-auto > p-6 w-full max-w-6xl mx-auto pb-24
 * - Header: gradient hero card with icon circle + text
 * - Tabs: pill-style segmented control
 * - Content: Tour categories + quick start cards + FAQ items (overview tab default)
 */

import React from 'react';
import { Skeleton } from './Skeleton';

const FaqItemSkeleton: React.FC = () => (
  <div className="border border-border rounded-xl overflow-hidden">
    <div className="w-full p-4 flex items-center justify-between">
      <Skeleton className="h-5 w-3/4" />
      <Skeleton className="h-5 w-5 rounded flex-shrink-0 ml-4" />
    </div>
  </div>
);

export const HelpSkeleton: React.FC = () => (
  <div className="h-full overflow-y-auto">
    <div className="p-6 w-full max-w-6xl mx-auto pb-24">
      {/* Hero Header */}
      <div className="mb-8 rounded-2xl bg-hover/50 px-8 py-8 flex items-center gap-6">
        <Skeleton className="w-16 h-16 rounded-2xl flex-shrink-0" />
        <div>
          <Skeleton className="h-8 w-36 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
      </div>

      {/* Tabs */}
      <div className="inline-flex gap-1 p-1 rounded-xl bg-hover/50 mb-8">
        <Skeleton className="h-10 w-28 rounded-lg" />
        <Skeleton className="h-10 w-32 rounded-lg" />
        <Skeleton className="h-10 w-36 rounded-lg" />
      </div>

      {/* Tour category skeletons */}
      <div className="mb-10">
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="border border-border rounded-xl p-4 border-l-4 border-l-hover shadow-soft flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="w-9 h-9 rounded-lg" />
                <Skeleton className="h-5 w-40" />
              </div>
              <Skeleton className="h-5 w-5 rounded" />
            </div>
          ))}
        </div>
      </div>

      {/* Quick Start cards skeleton */}
      <div className="mb-10">
        <Skeleton className="h-6 w-28 mb-4" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="border border-border rounded-xl p-5 shadow-soft">
              <Skeleton className="w-12 h-12 rounded-xl mb-3" />
              <Skeleton className="h-5 w-3/4 mb-1" />
              <Skeleton className="h-3 w-full" />
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div>
        <Skeleton className="h-7 w-64 mb-6" />
        <div className="space-y-3">
          {Array.from({ length: 7 }).map((_, i) => (
            <FaqItemSkeleton key={i} />
          ))}
        </div>
      </div>
    </div>
  </div>
);

export default HelpSkeleton;
